import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoManager = ({ onStatsUpdate }) => {
  const [videos, setVideos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '', category: '', duration: '', thumbnail: '', description: '', video_url: '', published: true
  });
  const { getAuthHeaders } = useAuth();
  const { toast } = useToast();

  useEffect(() => { fetchVideos(); }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos/videos`);
      setVideos(response.data);
      onStatsUpdate(response.data.length);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch videos", variant: "destructive" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVideo) {
        await axios.put(`${API}/admin/videos/videos/${editingVideo.id}`, formData, { headers: getAuthHeaders() });
        toast({ title: "Success!", description: "Video updated successfully" });
      } else {
        await axios.post(`${API}/admin/videos/videos`, formData, { headers: getAuthHeaders() });
        toast({ title: "Success!", description: "Video created successfully" });
      }
      setDialogOpen(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save video", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/admin/videos/videos/${id}`, { headers: getAuthHeaders() });
      toast({ title: "Success!", description: "Video deleted successfully" });
      fetchVideos();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete video", variant: "destructive" });
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({ title: video.title, category: video.category, duration: video.duration, thumbnail: video.thumbnail, description: video.description, video_url: video.video_url || '', published: video.published });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingVideo(null);
    setFormData({ title: '', category: '', duration: '', thumbnail: '', description: '', video_url: '', published: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Videos</h2>
        <Button className="btn-primary text-white" onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />Add New Video
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {videos.map((video) => (
          <Card key={video.id} className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-white text-lg">{video.title}</CardTitle>
                  <CardDescription className="text-gray-400">{video.category} • {video.duration}</CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" className="border-gray-700" onClick={() => handleEdit(video)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-gray-700 text-red-400" onClick={() => handleDelete(video.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm">{video.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl">
          <DialogHeader><DialogTitle>{editingVideo ? 'Edit Video' : 'Create New Video'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input placeholder="Title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-gray-800 border-gray-700 text-white" required />
            <Input placeholder="Category" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="bg-gray-800 border-gray-700 text-white" required />
            <Input placeholder="Duration (e.g., 10:30)" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} className="bg-gray-800 border-gray-700 text-white" required />
            <Input placeholder="Thumbnail URL" value={formData.thumbnail} onChange={(e) => setFormData({...formData, thumbnail: e.target.value})} className="bg-gray-800 border-gray-700 text-white" required />
            <Textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-gray-800 border-gray-700 text-white" rows={3} required />
            <Input placeholder="Video URL (optional)" value={formData.video_url} onChange={(e) => setFormData({...formData, video_url: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-700">Cancel</Button>
              <Button type="submit" className="btn-primary text-white">{editingVideo ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoManager;
